import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, IconButton, Box, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { getReferralOrderPreviewPdf } from '../../services/referralOrderService';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
// Configure pdfjs worker using local file copied by webpack
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
const PreviewReferralOrder = ({ open, onClose, patientId, requestBody }) => {
    const { t } = useTranslation('translation', {
        keyPrefix: 'PREVIEW_REFERRAL_ORDER'
    });
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const requestKey = useMemo(() => JSON.stringify(requestBody), [requestBody]);
    const lastFetchKeyRef = useRef(null);
    useEffect(() => {
        return () => {
            if (pdfBlobUrl)
                URL.revokeObjectURL(pdfBlobUrl);
        };
    }, [pdfBlobUrl]);
    useEffect(() => {
        if (!open)
            return;
        const fetchKey = `${patientId}::${requestKey}`;
        if (lastFetchKeyRef.current === fetchKey)
            return;
        lastFetchKeyRef.current = fetchKey;
        setPdfBlobUrl(null);
        setPdfLoading(true);
        setPdfError(null);
        setNumPages(0);
        getReferralOrderPreviewPdf(patientId, requestBody)
            .then((blob) => {
            const url = URL.createObjectURL(blob);
            setPdfBlobUrl(url);
            setPdfLoading(false);
        })
            .catch((error) => {
            setPdfError(error instanceof Error ? error.message : 'Failed to load PDF.');
            setPdfLoading(false);
        });
    }, [open, patientId, requestBody, requestKey]);
    const onDocumentLoadSuccess = ({ numPages: loadedNumPages }) => {
        setNumPages(loadedNumPages);
    };
    return (_jsxs(Dialog, { open: open, onClose: onClose, "aria-labelledby": "referral-order-preview", slotProps: {
            paper: {
                sx: {
                    width: '612px',
                    maxWidth: '612px',
                    height: '725px',
                    maxHeight: '725px',
                    borderRadius: 0,
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: 'none',
                    backgroundColor: '#fff'
                }
            }
        }, children: [_jsx(IconButton, { onClick: onClose, "aria-label": t('CLOSE_ARIA_LABEL'), sx: {
                    position: 'absolute',
                    top: 22,
                    right: 20,
                    zIndex: 2,
                    width: 48,
                    height: 48,
                    borderRadius: 0,
                    padding: 0,
                    backgroundColor: 'transparent',
                    '&:hover': { backgroundColor: '#F2F2F2', borderRadius: '50%' }
                }, children: _jsx(CloseIcon, { sx: { fontSize: 24, color: '#6D6D6D' } }) }), _jsx(DialogContent, { sx: {
                    p: 0,
                    width: '100%',
                    height: '100%',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    backgroundColor: '#fff'
                }, children: pdfLoading ? (_jsx(Box, { display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#fff", children: _jsx(CircularProgress, {}) })) : pdfBlobUrl ? (_jsx(Box, { sx: {
                        width: '100%',
                        minHeight: '100%',
                        backgroundColor: '#fff',
                        px: 0,
                        py: 0,
                        // Avoid any frame/border look.
                        border: 0,
                        outline: 0
                    }, children: _jsx(Document, { file: pdfBlobUrl, onLoadSuccess: onDocumentLoadSuccess, onLoadError: () => {
                            setPdfError('Failed to load PDF preview.');
                        }, loading: _jsx(Box, { display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#fff", children: _jsx(CircularProgress, {}) }), children: [...Array(numPages).keys()].map((index) => {
                            const pageNumber = index + 1;
                            return (_jsx(Box
                            // react-pdf wraps pages in a div; ensure no borders/margins show.
                            , { sx: {
                                    display: 'flex',
                                    justifyContent: 'center',
                                    backgroundColor: '#fff',
                                    '& .react-pdf__Page': { backgroundColor: '#fff' },
                                    '& canvas': { display: 'block', backgroundColor: '#fff' }
                                }, children: _jsx(Page, { pageNumber: pageNumber, width: 612, renderAnnotationLayer: false, renderTextLayer: false, loading: null }) }, `page_${pageNumber}`));
                        }) }) })) : pdfError ? (_jsx(Box, { display: "flex", justifyContent: "center", alignItems: "center", height: "100%", px: 2, bgcolor: "#fff", children: _jsx("div", { children: pdfError }) })) : null })] }));
};
export default PreviewReferralOrder;
